import { CONTACT_ID_UPLOAD_FIELDS } from '../constants/contactFields';
import type { ContactFormValues } from '../types/formSubmissions';
import type { QuestionSchema } from '../types/templateFormFields';
import {
    getContactBlockConfig,
    getContactBlockEntryTitle,
    getFilledContactBlockEntries,
} from './contactBlock';
import { splitPipeJoinedTableValue } from './intakeTableValue';
import { getVisibleQuestions } from './questionVisibility';

export interface UploadFileMapping {
    fileId: string;
    uploadFileTitle: string;
}

/** Upload answers are file ids joined by `|` (or `,` in older submissions). */
export function getDelimitedFileIds(value?: string | null): string[] {
    return (value ?? '')
        .split(/[|,]/)
        .map((fileId) => fileId.trim())
        .filter(Boolean);
}

function getUploadFileIdsFromValue(value: unknown): string[] {
    if (Array.isArray(value)) {
        return value.flatMap((item) => (typeof item === 'string' ? getDelimitedFileIds(item) : []));
    }
    return typeof value === 'string' ? getDelimitedFileIds(value) : [];
}

function getTableUploadFileMappings(
    schema: QuestionSchema,
    value: unknown,
    title: string,
): UploadFileMapping[] {
    const uploadColumns = (schema.columns ?? []).flatMap((column) =>
        column?.type === 'upload' && typeof column.key === 'string' && column.key
            ? [{ key: column.key, label: column.label?.trim() ? column.label : column.key }]
            : [],
    );
    if (uploadColumns.length === 0 || typeof value !== 'string' || !value) return [];

    const rows = splitPipeJoinedTableValue(value).flatMap((segment) => {
        try {
            const parsed: unknown = segment ? JSON.parse(segment) : [];
            return Array.isArray(parsed) ? (parsed as unknown[]) : [];
        } catch {
            return [];
        }
    });

    return rows.flatMap((row) => {
        if (!row || typeof row !== 'object' || Array.isArray(row)) return [];
        const rowRecord = row as Record<string, unknown>;
        return uploadColumns.flatMap((column) =>
            getUploadFileIdsFromValue(rowRecord[column.key]).map((fileId) => ({
                fileId,
                uploadFileTitle: `${title} - ${column.label}`,
            })),
        );
    });
}

export function getContactIdUploadFileMappings(
    contact: ContactFormValues,
    titlePrefix?: string,
): UploadFileMapping[] {
    return CONTACT_ID_UPLOAD_FIELDS.flatMap(({ field, title }) =>
        getDelimitedFileIds(contact[field]).map((fileId) => ({
            fileId,
            uploadFileTitle: titlePrefix ? `${titlePrefix} - ${title}` : title,
        })),
    );
}

function getContactBlockUploadFileMappings(
    schema: QuestionSchema,
    value: unknown,
    title: string,
): UploadFileMapping[] {
    const config = getContactBlockConfig(schema);
    return getFilledContactBlockEntries(value, schema).flatMap((entry, index, entries) => {
        const entryTitle = `${title} - ${getContactBlockEntryTitle(config.entryLabel, index, entries.length)}`;
        const nestedMappings = getVisibleQuestions(
            config.questions ?? [],
            (question) => entry.answers[question.questionSchema.name],
        ).flatMap((question) =>
            getFieldUploadFileMappings(
                question.questionSchema,
                entry.answers[question.questionSchema.name],
                `${entryTitle} - ${question.title}`,
            ),
        );
        return [...getContactIdUploadFileMappings(entry.contact, entryTitle), ...nestedMappings];
    });
}

/**
 * Every uploaded file in a field's answer, titled from `title`. Handles plain uploads, upload
 * columns in tables, and contact blocks (ID uploads plus nested question uploads per entry).
 */
export function getFieldUploadFileMappings(
    schema: QuestionSchema | null | undefined,
    value: unknown,
    title: string,
): UploadFileMapping[] {
    switch (schema?.type) {
        case 'upload':
            return getUploadFileIdsFromValue(value).map((fileId) => ({
                fileId,
                uploadFileTitle: title,
            }));
        case 'table':
            return getTableUploadFileMappings(schema, value, title);
        case 'contactInfoSection':
            return getContactBlockUploadFileMappings(schema, value, title);
        default:
            return [];
    }
}
